import React, { useEffect, useRef, useState, useCallback } from "react";
import { Check, X, ZoomIn, Move } from "lucide-react";

// Lightweight image cropper (drag + zoom) with fixed aspect ratio.
// Props:
//  - file: File selected by the user
//  - aspect: crop aspect ratio (width / height) e.g. 1 (logo) or 3 (banner)
//  - title: modal title
//  - outputWidth: target output width in px (height derived from aspect)
//  - onCancel(), onCropped(blob)
export default function ImageCropper({ file, aspect = 1, title = "Recadrer l'image", outputWidth = 800, circular = false, onCancel, onCropped }) {
  const [imgEl, setImgEl] = useState(null);
  const [imgSrc, setImgSrc] = useState("");
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const dragState = useRef(null);
  const baseRef = useRef({ scale: 1, boxW: 0, boxH: 0 });

  // Crop box display dimensions (fit within a max width)
  const MAX_W = 460;
  const boxW = Math.min(MAX_W, aspect >= 1 ? MAX_W : MAX_W * aspect);
  const boxH = boxW / aspect;

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImgSrc(url);
    const img = new Image();
    img.onload = () => setImgEl(img);
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Compute base cover-scale and center the image whenever image/box changes
  useEffect(() => {
    if (!imgEl) return;
    const scale = Math.max(boxW / imgEl.width, boxH / imgEl.height);
    baseRef.current = { scale, boxW, boxH };
    setZoom(1);
    const dispW = imgEl.width * scale;
    const dispH = imgEl.height * scale;
    setOffset({ x: (boxW - dispW) / 2, y: (boxH - dispH) / 2 });
  }, [imgEl, boxW, boxH]);

  const clampOffset = useCallback((x, y, z) => {
    const { scale } = baseRef.current;
    const dispW = imgEl.width * scale * z;
    const dispH = imgEl.height * scale * z;
    const minX = boxW - dispW;
    const minY = boxH - dispH;
    return {
      x: Math.min(0, Math.max(minX, x)),
      y: Math.min(0, Math.max(minY, y)),
    };
  }, [imgEl, boxW, boxH]);

  const onPointerDown = (e) => {
    if (!imgEl) return;
    const pt = e.touches ? e.touches[0] : e;
    dragState.current = { startX: pt.clientX, startY: pt.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!dragState.current) return;
    const pt = e.touches ? e.touches[0] : e;
    const dx = pt.clientX - dragState.current.startX;
    const dy = pt.clientY - dragState.current.startY;
    setOffset(clampOffset(dragState.current.ox + dx, dragState.current.oy + dy, zoom));
  };
  const onPointerUp = () => { dragState.current = null; };

  const onZoomChange = (z) => {
    // keep image centered on crop-box center while zooming
    const { scale } = baseRef.current;
    const cx = boxW / 2, cy = boxH / 2;
    const imgCx = (cx - offset.x) / (scale * zoom);
    const imgCy = (cy - offset.y) / (scale * zoom);
    const newX = cx - imgCx * scale * z;
    const newY = cy - imgCy * scale * z;
    setZoom(z);
    setOffset(clampOffset(newX, newY, z));
  };

  const confirm = async () => {
    if (!imgEl) return;
    setBusy(true);
    try {
      const { scale } = baseRef.current;
      const factor = scale * zoom;
      const sx = -offset.x / factor;
      const sy = -offset.y / factor;
      const sw = boxW / factor;
      const sh = boxH / factor;

      const outW = outputWidth;
      const outH = Math.round(outputWidth / aspect);
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(imgEl, sx, sy, sw, sh, 0, 0, outW, outH);

      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
      onCropped(blob);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="image-cropper-modal">
      <div className="bg-[#121212] border border-[#333] rounded-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#333]">
          <h3 className="font-bold text-white flex items-center gap-2"><Move className="w-4 h-4 text-[#FF6B35]" /> {title}</h3>
          <button onClick={onCancel} data-testid="cropper-cancel-btn" className="text-[#888] hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 flex flex-col items-center gap-4">
          <p className="text-xs text-[#888] text-center">Glissez pour déplacer, utilisez le curseur pour zoomer.</p>
          <div
            ref={boxRef}
            className={`relative overflow-hidden border border-[#444] bg-[#0a0a0a] touch-none select-none cursor-move ${circular ? 'rounded-full' : 'rounded-lg'}`}
            style={{ width: boxW, height: boxH }}
            onMouseDown={onPointerDown}
            onMouseMove={onPointerMove}
            onMouseUp={onPointerUp}
            onMouseLeave={onPointerUp}
            onTouchStart={onPointerDown}
            onTouchMove={onPointerMove}
            onTouchEnd={onPointerUp}
            data-testid="cropper-viewport"
          >
            {imgEl && (
              <img
                src={imgSrc}
                alt="crop"
                draggable={false}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: imgEl.width * baseRef.current.scale * zoom,
                  height: imgEl.height * baseRef.current.scale * zoom,
                  transform: `translate(${offset.x}px, ${offset.y}px)`,
                  transformOrigin: "top left",
                  maxWidth: "none",
                }}
              />
            )}
            {/* grid overlay */}
            <div className="absolute inset-0 pointer-events-none" style={{
              backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.15) 1px, transparent 1px)",
              backgroundSize: `${boxW / 3}px ${boxH / 3}px`,
            }} />
          </div>

          <div className="flex items-center gap-3 w-full max-w-[460px]">
            <ZoomIn className="w-4 h-4 text-[#888] flex-shrink-0" />
            <input
              type="range" min="1" max="4" step="0.01" value={zoom}
              onChange={(e) => onZoomChange(parseFloat(e.target.value))}
              data-testid="cropper-zoom-slider"
              className="w-full accent-[#FF6B35] cursor-pointer"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#333]">
          <button onClick={onCancel} data-testid="cropper-cancel-action"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-[#B0B0B0] hover:text-white transition-colors">
            Annuler
          </button>
          <button onClick={confirm} disabled={busy || !imgEl} data-testid="cropper-confirm-btn"
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold text-white transition-colors ${busy || !imgEl ? "bg-[#333] cursor-not-allowed" : "bg-[#FF6B35] hover:bg-[#E55A25]"}`}>
            {busy ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Check className="w-4 h-4" />}
            Valider
          </button>
        </div>
      </div>
    </div>
  );
}
